import { useEffect, useState } from 'react'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import DialogTitle from '@mui/joy/DialogTitle'
import DialogContent from '@mui/joy/DialogContent'
import Stack from '@mui/joy/Stack'
import Input from '@mui/joy/Input'
import Button from '@mui/joy/Button'
import Alert from '@mui/joy/Alert'
import Typography from '@mui/joy/Typography'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import FormHelperText from '@mui/joy/FormHelperText'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import VideocamIcon from '@mui/icons-material/Videocam'

import { startBot } from '../../api/dsGmeetApi'
import { useCurrentUserId } from '../../hooks/useCurrentUserId'

interface Props {
    open: boolean
    onClose: () => void
    onCreated: (botId: string) => void
}

export function RecordCallModal({ open, onClose, onCreated }: Props) {
    const userId = useCurrentUserId()
    const [meetingUrl, setMeetingUrl] = useState('')
    const [pending, setPending] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (open) {
            setMeetingUrl('')
            setError('')
        }
    }, [open])

    const submit = async () => {
        setError('')
        const url = meetingUrl.trim()
        if (!url) {
            setError('Meeting URL is required.')
            return
        }
        if (!userId) {
            setError('You must be signed in.')
            return
        }
        setPending(true)
        try {
            const { botId } = await startBot({ meetingUrl: url, userId })
            onCreated(botId)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setPending(false)
        }
    }

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ width: 'min(560px, 95vw)' }}>
                <ModalClose />
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <VideocamIcon />
                        Record this call
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <Typography level="body-sm" color="neutral">
                            Paste a Google Meet, Zoom, Microsoft Teams, or Webex URL. A Recall bot will join the meeting, capture audio, and produce a
                            multilingual transcript.
                        </Typography>
                        {error && <Alert color="danger">{error}</Alert>}
                        <FormControl>
                            <FormLabel>Meeting URL</FormLabel>
                            <Input
                                placeholder="https://meet.google.com/abc-defg-hij"
                                value={meetingUrl}
                                onChange={(e) => setMeetingUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !pending) void submit()
                                }}
                                autoFocus
                            />
                            <FormHelperText>The bot joins as a visible participant and notifies attendees it is recording.</FormHelperText>
                        </FormControl>
                        <Stack direction="row" justifyContent="flex-end" gap={1}>
                            <Button variant="plain" color="neutral" onClick={onClose} disabled={pending}>
                                Cancel
                            </Button>
                            <Button color="danger" startDecorator={<FiberManualRecordIcon />} loading={pending} disabled={pending || !userId} onClick={submit}>
                                Start recording
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    )
}
